
import { StudioAction } from '@/components/ui/StudioAction';

import contentStyles from './PublicContent.module.css';
import { PublicContentFooter } from './PublicContentFooter';
import { NextIntlClientProvider, type AbstractIntlMessages } from 'next-intl';
import type { Locale } from '@/i18n/locales';
import { StyleDiscoveryProof } from './StyleDiscoveryProof';
import Link from '@/components/auth/LoginLink';
import {
  ArrowRight,
  CheckCircle2,
  ExternalLink,
  ImageIcon
} from 'lucide-react';
import { SeoPublicHeader, type SeoPublicHeaderMessages } from '@/components/public-site/SeoPublicHeader';
import { GuideArticleHeroSlideshow } from '@/components/public-site/GuideArticleHeroSlideshow';
import { GuideFeaturedSlideshow } from '@/components/public-site/GuideFeaturedSlideshow';
import {
  SEO_GUIDE_AUTHOR,
  SEO_GUIDE_MODIFIED_DATE,
  SEO_GUIDE_PUBLISHED_DATE,
  type SeoGuideArticle,
  type SeoGuideLocale,
} from '@/lib/seo/guides';
import { buildLocalizedSeoPath } from '@/lib/seo/site';
import { guideHrefForLocale } from '@/lib/seo/guide-href';
import enMessages from '../../../messages/en.json';
import zhMessages from '../../../messages/zh.json';


const copy = {
  zh: zhMessages.seoGuides.common,
  en: enMessages.seoGuides.common,
} satisfies Record<SeoGuideLocale, Record<string, string>>;

const homepageCopy = {
  zh: zhMessages.seoHome,
  en: enMessages.seoHome,
} satisfies Record<SeoGuideLocale, SeoPublicHeaderMessages>;

interface SeoGuideShellProps {
  locale: Locale;
  guideMessages?: { common: Record<string, string>; header: SeoPublicHeaderMessages; styleMessages?: AbstractIntlMessages };
  children: React.ReactNode;
  startHref?: string;
  languagePath?: string;
}

interface SeoGuideIndexPageProps {
  locale: Locale;
  guideMessages?: { common: Record<string, string>; header: SeoPublicHeaderMessages; styleMessages?: AbstractIntlMessages };
  guides: SeoGuideArticle[];
}

interface SeoGuideArticlePageProps {
  locale: Locale;
  guideMessages?: { common: Record<string, string>; header: SeoPublicHeaderMessages; styleMessages?: AbstractIntlMessages };
  guide: SeoGuideArticle;
  relatedGuides: SeoGuideArticle[];
  jsonLd: string;
}

function GuideIndexCard({
  guide,
  href,
  readGuideLabel,
}: {
  guide: SeoGuideArticle;
  href: string;
  readGuideLabel: string;
}) {
  return (
    <Link
      href={href}
      className="group relative block min-w-0 overflow-hidden rounded-[12px] border border-black/[0.07] bg-[#ECE7DF] shadow-[0_18px_52px_rgba(23,25,28,0.08)] transition duration-300 hover:-translate-y-1 hover:shadow-[0_24px_64px_rgba(23,25,28,0.12)]"
    >
      <div className="relative aspect-[4/5] overflow-hidden">
        <img src={guide.heroImage} alt={guide.heroImageAlt} width={900} height={1125} loading="lazy" className="absolute inset-0 h-full w-full object-cover object-center transition duration-500 group-hover:scale-[1.025]" />
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#071820]/95 via-[#071820]/78 to-transparent px-5 pb-5 pt-40 text-white">
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-white/75">{guide.primaryKeyword}</p>
          <h2 className="mt-2 text-[19px] font-semibold leading-[1.15] tracking-[-0.025em] text-white">{guide.title}</h2>
          <p className="mt-2 text-[12px] leading-5 text-white/75">{guide.description}</p>
          <span className="mt-4 inline-flex items-center gap-2 text-[13px] font-semibold text-white">
            {readGuideLabel}
            <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
          </span>
        </div>
      </div>
    </Link>
  );
}

function SeoGuideShell({
  locale,
  children,
  guideMessages,
  startHref = '/login?redirect=%2Fexplore&login=1',
  languagePath = '/guides',
}: SeoGuideShellProps) {
  return (
    <NextIntlClientProvider locale={locale} timeZone="Asia/Shanghai" messages={guideMessages?.styleMessages ?? {
      auth: { cinematic: (locale === 'zh' ? zhMessages : enMessages).auth.cinematic },
      seoLanding: { styleDiscovery: (locale === 'zh' ? zhMessages : enMessages).seoLanding.styleDiscovery },
    }}>
    <main className={`${contentStyles.page} ${contentStyles.seoPage} ${contentStyles.guidePage} seo-public seo-guide-public min-h-screen overflow-x-clip bg-white text-[#203644] antialiased`}>
      <SeoPublicHeader
        locale={locale}
        messages={guideMessages?.header ?? homepageCopy[locale === 'zh' ? 'zh' : 'en']}
        startHref={startHref}
        languagePath={languagePath}
      />
      {children}
      <PublicContentFooter locale={locale} />
    </main>
    </NextIntlClientProvider>
  );
}

export function SeoGuideIndexPage({ locale, guides, guideMessages }: SeoGuideIndexPageProps) {
  const t = guideMessages?.common ?? copy[locale === 'zh' ? 'zh' : 'en'];
  const featured = guides[0];
  const localize = (path: string) => buildLocalizedSeoPath(path, locale);

  return (
    <SeoGuideShell guideMessages={guideMessages} locale={locale}>
      <section className="mx-auto grid max-w-[1300px] items-start gap-12 px-5 py-14 md:grid-cols-[0.92fr_1.08fr] md:px-8 md:py-20 lg:gap-20 lg:py-12">
        <div>
          <p className="flex w-fit items-center gap-2 text-[15px] font-medium leading-5 text-[#168FC7] before:h-1.5 before:w-1.5 before:rounded-full before:bg-[#4ABDE9]">{t.allGuides}</p>
          <h1 className="mt-5 max-w-[620px] text-[44px] font-medium leading-[0.98] tracking-[-0.055em] text-[#203644] sm:text-[52px] md:text-[58px] lg:text-[68px]">
            {t.introTitle}
          </h1>
          <p className="mt-6 max-w-[590px] text-[16px] font-normal leading-7 text-[#5B6A73] md:text-[18px] md:leading-8">
            {t.introBody}
          </p>
        </div>
        <GuideFeaturedSlideshow
          guide={featured}
          href={localize(featured.path)}
          readGuideLabel={t.readGuide}
          pauseLabel={t.pauseSlideshow}
          resumeLabel={t.resumeSlideshow}
        />
      </section>

      <section className="mx-auto grid max-w-[1300px] items-stretch gap-4 px-5 pb-20 md:grid-cols-2 md:px-8 md:pb-28 lg:grid-cols-4">
        {guides.slice(1).map((guide) => (
          <GuideIndexCard
            key={guide.slug}
            guide={guide}
            href={localize(guide.path)}
            readGuideLabel={t.readGuide}
          />
        ))}
      </section>
    </SeoGuideShell>
  );
}

export function SeoGuideArticlePage({ locale, guide, relatedGuides, jsonLd, guideMessages }: SeoGuideArticlePageProps) {
  const t = guideMessages?.common ?? copy[locale === 'zh' ? 'zh' : 'en'];
  const localize = (path: string) => buildLocalizedSeoPath(path, locale);
  const startHref = guideHrefForLocale(guide.startHref ?? `/login?redirect=%2Fexplore&login=1&source_landing=${encodeURIComponent(guide.landingPath)}`, locale);
  const dateFormatter = new Intl.DateTimeFormat(locale, {
    dateStyle: 'long',
    timeZone: 'UTC',
  });
  const publishedDateValue = guide.publishedDate ?? SEO_GUIDE_PUBLISHED_DATE;
  const publishedDate = dateFormatter.format(new Date(`${publishedDateValue}T00:00:00Z`));
  const modifiedDateValue = guide.modifiedDate ?? SEO_GUIDE_MODIFIED_DATE;
  const modifiedDate = dateFormatter.format(new Date(`${modifiedDateValue}T00:00:00Z`));
  const isImageToVideoGuide = guide.slug === 'image-to-video-fashion-ads';
  const isReferenceImageGuide = guide.slug === 'reference-image-ai-generation-workflow';
  const hasStandaloneHero = Boolean(guide.heroSlides) || isImageToVideoGuide;

  return (
    <SeoGuideShell guideMessages={guideMessages} locale={locale} startHref={startHref} languagePath={guide.path}>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: jsonLd }}
      />
      <article>
        <section className="mx-auto grid max-w-[1300px] items-start gap-12 px-5 py-14 md:grid-cols-[0.92fr_1.08fr] md:px-8 md:py-20 lg:gap-20 lg:py-12">
          <div>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
              <Link href={localize('/guides')} className="flex w-fit items-center gap-2 text-[17px] font-medium leading-6 text-[#168FC7] md:text-[18px] md:leading-7">
                <ArrowRight className="h-5 w-5 rotate-180" />
                {t.back}
              </Link>
              <p className="flex w-fit items-center gap-2 text-[16px] font-medium leading-6 text-[#168FC7] before:h-2 before:w-2 before:rounded-full before:bg-[#4ABDE9] md:text-[17px] md:leading-7">{guide.primaryKeyword}</p>
            </div>
            <h1 className="mt-5 max-w-[620px] text-balance text-[44px] font-medium leading-[0.98] tracking-[-0.055em] text-[#203644] sm:text-[52px] md:text-[58px] lg:text-[68px]">
              {guide.title}
            </h1>
            <p className="mt-6 max-w-[590px] text-[16px] font-normal leading-7 text-[#5B6A73] md:text-[18px] md:leading-8">
              {guide.description}
            </p>
            <p className="mt-6 text-[13px] font-normal text-[#5B6A73]">
              <span>{t.editorialTeam || SEO_GUIDE_AUTHOR}</span>
              <span className="mx-2 text-[#203644]/24">·</span>
              {guide.readingTime}
              <span className="mx-2 text-[#203644]/24">·</span>
              {t.updated} <time dateTime={modifiedDateValue}>{modifiedDate}</time>
              <time className="sr-only" dateTime={publishedDateValue}>{t.published} {publishedDate}</time>
            </p>
          </div>
          <figure className={isImageToVideoGuide ? 'mx-auto flex h-full w-full max-w-[540px] flex-col self-stretch' : hasStandaloneHero ? 'mx-auto w-full max-w-[540px]' : 'overflow-hidden rounded-[12px] border border-black/[0.07] bg-white p-3 shadow-[0_26px_80px_rgba(23,25,28,0.10)]'}>
            {guide.heroSlides ? (
              <GuideArticleHeroSlideshow slides={guide.heroSlides} label={t.heroImages} pauseLabel={t.pauseSlideshow} resumeLabel={t.resumeSlideshow} />
            ) : isImageToVideoGuide ? (
              <div className="relative min-h-[420px] flex-1 overflow-hidden rounded-[18px] bg-[#DCEEF4] shadow-[0_28px_80px_rgba(23,25,28,0.16)]">
                <video
                  src="/guides/image-to-video-fashion-hero.mp4"
                  poster={guide.heroImage}
                  controls
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  aria-label={guide.heroImageAlt}
                  className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[#071820]/20 to-transparent" />
              </div>
            ) : isReferenceImageGuide ? (
              <div className="grid aspect-[16/10] grid-cols-2 gap-2 overflow-hidden rounded-[10px] bg-[#F2EEE8]">
                <img
                  src="/guides/reference-image/comparison-3.webp"
                  alt={guide.heroImageAlt}
                  width={1082}
                  height={1454}
                  className="h-full w-full object-cover object-center"
                />
                <img
                  src="/guides/reference-image/comparison-4.webp"
                  alt={guide.heroImageAlt}
                  width={1081}
                  height={1455}
                  className="h-full w-full object-cover object-center"
                />
              </div>
            ) : (
              <img src={guide.heroImage} alt={guide.heroImageAlt} width={guide.heroImageWidth ?? 1600} height={guide.heroImageHeight ?? 900} className={guide.heroImageWidth ? "max-h-[560px] w-full rounded-[10px] object-contain" : "aspect-[16/10] w-full rounded-[10px] object-cover object-top"} />
            )}
            <figcaption className={hasStandaloneHero ? `${isImageToVideoGuide ? 'shrink-0 ' : ''}px-1 pt-4 text-[12px] font-normal leading-5 text-[#5B6A73]` : 'px-2.5 pb-1 pt-3 text-[12px] font-normal leading-5 text-[#5B6A73]'}>{guide.heroImageAlt}</figcaption>
          </figure>
        </section>

        {guide.slug === 'product-photography-ideas' && <StyleDiscoveryProof locale={locale} sourcePath={guide.path} />}

        <section className={`${contentStyles.guideLayout} mx-auto grid max-w-[1300px] gap-10 px-5 pb-20 md:px-8 md:pb-28`}>
          <aside className={contentStyles.guideContents}>
            <div className="rounded-[16px] border border-[#D9E0E3] bg-[#F2F3F5] p-5">
              <p className="text-[13px] font-medium uppercase tracking-[0.16em] text-[#168FC7]">{t.contents}</p>
              <ul className="mt-4 space-y-3">
                {guide.sections.map((section) => (
                  <li key={section.id}>
                    <a href={`#${section.id}`} className="text-[13px] font-normal leading-5 text-[#5B6A73] transition hover:text-[#203644]">
                      {section.title}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          <div className={contentStyles.guideSections}>
            <section className="rounded-[12px] border border-[#BDEAF5] bg-[#EAF8FC] p-6 shadow-[0_24px_70px_rgba(74,189,233,0.12)]">
              <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-[10px] bg-white/72 text-[#168FC7]">
                <ImageIcon className="h-5 w-5" />
              </div>
              <h2 className="text-[26px] font-medium leading-8 tracking-[-0.035em] text-[#203644]">{guide.takeawaysTitle}</h2>
              <ul className="mt-5 grid gap-3">
                {guide.takeaways.map((item) => (
                  <li key={item} className="flex gap-3 text-[14px] font-normal leading-7 text-[#5B6A73]">
                    <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-[#168FC7]" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </section>

            {guide.sections.map((section) => (
              <section key={section.id} id={section.id} className={contentStyles.guideSection}>
                <h2 className="text-[28px] font-medium leading-9 tracking-[-0.035em] text-[#203644]">{section.title}</h2>
                <div className="mt-5 space-y-5 text-[16px] font-normal leading-8 text-[#5B6A73]">
                  {section.paragraphs.map((paragraph) => (
                    <p key={paragraph}>{paragraph}</p>
                  ))}
                </div>
                {section.example ? (
                  <div className="mt-6 rounded-[10px] border border-[#BDEAF5] bg-[#F0FAFD] p-5">
                    <p className="text-sm font-semibold text-[#142235]">{section.example.label}</p>
                    <pre className="mt-3 whitespace-pre-wrap break-words font-sans text-[15px] leading-7 text-[#34485B]">{section.example.text}</pre>
                  </div>
                ) : null}
                {section.links?.length ? (
                  <div className="mt-6 flex flex-wrap gap-3">
                    {section.links.map((link) => (
                      <StudioAction key={link.href} href={guideHrefForLocale(link.href, locale)} variant="secondary" className={contentStyles.action}>
                        {link.label}<ArrowRight className="h-4 w-4 shrink-0" aria-hidden />
                      </StudioAction>
                    ))}
                  </div>
                ) : null}
                {section.bullets?.length ? (
                  <ul className="mt-5 grid gap-3">
                    {section.bullets.map((item) => (
                      <li key={item} className="flex gap-3 text-[14px] font-normal leading-7 text-[#5B6A73]">
                        <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-[#168FC7]" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                ) : null}
                {section.table ? (
                  <div className={`mt-7 ${contentStyles.tableScroll}`} tabIndex={0} role="region" aria-label={section.table.title}>
                    <h3 className="mb-4 text-[20px] font-medium leading-7 tracking-[-0.025em] text-[#203644]">{section.table.title}</h3>
                    <table className="w-full min-w-[680px] border-separate border-spacing-0 text-left text-sm">
                      <thead>
                        <tr>
                          {section.table.headers.map((header) => (
                            <th scope="col" key={header} className="border-b border-[#D5EAF1] bg-[#F2FAFD] px-4 py-3 text-[13px] font-medium text-[#203644]">{header}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {section.table.rows.map((row) => (
                          <tr key={row.join('|')}>
                            {row.map((cell) => (
                              <td key={cell} className="border-b border-[#E5EEF2] px-4 py-4 text-[14px] font-normal leading-6 text-[#5B6A73]">{cell}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : null}
                {section.visuals?.length ? (
                  <div className={`mt-7 grid gap-4 ${section.visuals.length > 1 ? 'sm:grid-cols-2' : ''}`}>
                    {section.visuals.map((visual) => (
                      <figure key={`${visual.src}-${visual.caption}`} className="flex min-w-0 flex-col overflow-hidden rounded-[10px] border border-black/[0.07] bg-[#F7F8F8] p-2">
                        <div className="flex min-h-[260px] flex-1 items-center justify-center overflow-hidden rounded-[8px] bg-white p-2 md:min-h-[340px]">
                          {visual.mediaType === 'video' ? (
                            <video
                              src={visual.src}
                              aria-label={visual.alt}
                              controls
                              playsInline
                              preload="metadata"
                              className="max-h-[680px] w-full object-contain"
                            />
                          ) : (
                            <img
                              src={visual.src}
                              alt={visual.alt}
                              width={visual.width}
                              height={visual.height}
                              loading="lazy"
                              className="max-h-[680px] w-full object-contain"
                            />
                          )}
                        </div>
                        <figcaption className="px-2 py-3 text-[12px] font-normal leading-5 text-[#5B6A73]">
                          {visual.caption}
                        </figcaption>
                      </figure>
                    ))}
                  </div>
                ) : null}
              </section>
            ))}

            {guide.commonMistakes?.length ? (
              <section className="rounded-[12px] border border-[#F3D9C8] bg-[#FFF7F2] p-6 md:p-8">
                <h2 className="text-[26px] font-medium leading-8 tracking-[-0.035em] text-[#203644]">
                  {guide.commonMistakesTitle}
                </h2>
                <ul className="mt-5 grid gap-3">
                  {guide.commonMistakes.map((item) => (
                    <li key={item} className="flex gap-3 text-[14px] font-normal leading-7 text-[#5B6A73]">
                      <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-[#D87948]" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            <section className="rounded-[12px] border border-black/[0.07] bg-white p-6 md:p-8">
              <p className="text-[12px] font-medium uppercase tracking-[0.16em] text-[#168FC7]">{t.table}</p>
              <h2 className="mt-3 text-[26px] font-medium leading-8 tracking-[-0.035em] text-[#203644]">{guide.table.title}</h2>
              <div className={`mt-5 ${contentStyles.tableScroll}`} tabIndex={0} role="region" aria-label={guide.table.title}>
                <table className="w-full min-w-[680px] border-separate border-spacing-0 text-left text-sm">
                  <thead>
                    <tr>
                      {guide.table.headers.map((header) => (
                        <th scope="col" key={header} className="border-b border-[#D5EAF1] bg-[#F2FAFD] px-4 py-3 text-[13px] font-medium text-[#203644]">{header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {guide.table.rows.map((row) => (
                      <tr key={row.join('|')}>
                        {row.map((cell) => (
                          <td key={cell} className="border-b border-[#E5EEF2] px-4 py-4 text-[14px] font-normal leading-6 text-[#5B6A73]">{cell}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="grid gap-5 md:grid-cols-2">
              <div className="rounded-[12px] border border-black/[0.07] bg-white p-6">
                <p className="text-[12px] font-medium uppercase tracking-[0.16em] text-[#168FC7]">{t.checklist}</p>
                <h2 className="mt-3 text-[24px] font-medium leading-8 tracking-[-0.035em] text-[#203644]">{guide.checklistTitle}</h2>
                <ul className="mt-5 space-y-3">
                  {guide.checklist.map((item) => (
                    <li key={item} className="flex gap-3 text-[14px] font-normal leading-7 text-[#5B6A73]">
                      <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-[#168FC7]" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-[12px] border border-black/[0.07] bg-white p-6">
                <p className="text-[12px] font-medium uppercase tracking-[0.16em] text-[#168FC7]">{guide.sourceEyebrow ?? t.sources}</p>
                <h2 className="mt-3 text-[24px] font-medium leading-8 tracking-[-0.035em] text-[#203644]">{guide.sourceTitle}</h2>
                <ul className="mt-5 space-y-3">
                  {guide.sources.map((source) => (
                    <li key={source.href}>
                      <a href={source.href} target="_blank" rel="noopener noreferrer" className="inline-flex items-start gap-2 text-sm font-medium leading-6 text-[#168FC7] transition hover:text-[#0D749D]">
                        {source.label}
                        <ExternalLink className="mt-1 h-3.5 w-3.5 shrink-0" />
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </section>

            <section className="rounded-[12px] border border-black/[0.07] bg-white p-6 md:p-8">
              <p className="text-[12px] font-medium uppercase tracking-[0.16em] text-[#168FC7]">{t.faq}</p>
              <div className="mt-5 grid gap-3">
                {guide.faq.map((item) => (
                  <details key={item.question} className="group rounded-[10px] bg-[#F7F8F8] p-5">
                    <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-[14px] font-medium text-[#203644]">
                      {item.question}
                      <span className="text-[#168FC7] transition-transform group-open:rotate-45">+</span>
                    </summary>
                    <p className="mt-3 text-[14px] font-normal leading-7 text-[#5B6A73]">{item.answer}</p>
                  </details>
                ))}
              </div>
            </section>

            <section className="rounded-[17px] border border-[#D9E0E3] bg-[#F2F3F5] p-6 text-[#203644] md:p-8">
              <h2 className="text-[30px] font-medium leading-9 tracking-[-0.035em]">{guide.ctaTitle}</h2>
              <p className="mt-4 max-w-2xl text-[14px] font-normal leading-7 text-[#5B6A73]">{guide.ctaBody}</p>
              <StudioAction href={guideHrefForLocale(guide.startHref ?? guide.landingPath, locale)} variant="primary" className={`mt-6 ${contentStyles.action}`}>
                {guide.ctaLabel}
                <ArrowRight className="h-4 w-4" />
              </StudioAction>
            </section>

            <section>
              <p className="text-[12px] font-medium uppercase tracking-[0.16em] text-[#168FC7]">{t.related}</p>
              <div className="mt-4 grid gap-4 md:grid-cols-2">
                {relatedGuides.map((related) => (
                  <Link key={related.slug} href={localize(related.path)} className="group rounded-[12px] border border-black/[0.07] bg-white p-5 transition hover:-translate-y-1 hover:shadow-[0_22px_60px_rgba(23,25,28,0.09)]">
                    <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-[#168FC7]">{related.primaryKeyword}</p>
                    <h3 className="mt-3 text-[18px] font-semibold leading-7 tracking-[-0.02em] text-[#203644]">{related.title}</h3>
                    <span className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-[#168FC7]">
                      {t.readGuide}
                      <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" />
                    </span>
                  </Link>
                ))}
              </div>
            </section>
          </div>
        </section>
      </article>
    </SeoGuideShell>
  );
}
